import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config/api";
import { useTheme, getColors } from "../ThemeContext";

/* ──────────────────────────────────────────────────────────────
   PlotSummary  (toggle)
   Put it inside any chart card, AFTER the chart block:

     <div style={{ height }}>{children}</div>
     <PlotSummary title={title} subtitle={subtitle}>{children}</PlotSummary>

   Card must be position: relative. It reads the Recharts chart that
   is inside `children`, works out what THAT plot is about, and shows a
   field-engineer summary (what it means for the well, what is driving
   it, what to do at the wellsite) under the chart when switched on.
   Backend = /api/plot-summary. If it is down, a local summary built
   from the same plot data + the same plot-specific lens is shown.
   ────────────────────────────────────────────────────────────── */

const MONO = "'Space Mono', monospace";

const CHART_TYPES = new Set([
  "LineChart", "BarChart", "AreaChart", "ComposedChart",
  "ScatterChart", "PieChart", "RadarChart", "RadialBarChart",
]);
const SERIES_TYPES = new Set(["Line", "Bar", "Area", "Scatter", "Pie", "Radar", "RadialBar"]);

const summaryCache = {};
const SUMMARY_TIMEOUT_MS = 20000;
const INLINE_GAP_PX = 28;
const SUMMARY_LABEL_PX = 14;

const nameOf = (el) =>
  (el && el.type && (el.type.displayName || el.type.name)) || "";
const isNum = (v) => typeof v === "number" && isFinite(v);
const rnd = (v) => {
  if (!isNum(v)) return v;
  const a = Math.abs(v);
  if (a >= 1000) return Math.round(v);
  if (a >= 100) return Math.round(v * 10) / 10;
  return Math.round(v * 100) / 100;
};
const labelText = (lab) => {
  if (!lab) return "";
  if (typeof lab === "string") return lab;
  if (typeof lab === "object" && typeof lab.value === "string") return lab.value;
  return "";
};

/* ═════════════ PLOT-SPECIFIC FIELD-ENGINEER LENSES ═════════════
   Each plot is matched (title + subtitle + series + axis names) to ONE
   lens. The lens says what a field engineer uses that plot for, which
   factors matter for it, and what to do when it moves up / down.
   First match wins, so the specific ones come first.            */
const LENSES = [
  {
    id: "ml-training", re: /loss|epoch|tensorboard|logged variables|scalar|training/,
    role: "how far the AI model can be trusted before you act on its forecasts or setpoints",
    factors: ["gap between training and validation loss", "whether the curve has flattened or is still falling", "sudden spikes (bad data or drift)"],
    up: "Loss is climbing: do not accept AI setpoints blindly, cross-check against the last good cycles on site.",
    down: "Loss is falling: the model is learning; forecasts can be used with normal operator checks.",
    flat: "Loss has flattened: the model has settled; use its forecasts, and re-check after any change in well behaviour.",
  },
  {
    id: "anomaly", re: /anomal|sigma|alarm|physics.?violation|rules broken|passed all equations|abnormal/,
    role: "which cycles behaved abnormally and whether the well needs a site visit",
    factors: ["arrival time and velocity against this well's own baseline", "casing/tubing/line pressure at open", "fluid recovery after the plunger lands", "sensor or valve faults that mimic anomalies"],
    up: "Flags are rising: send the pumper to check plunger, valve and separator, and confirm the sensors read correctly.",
    down: "Flags are easing: the well is returning to its normal pattern; keep watching the next few cycles.",
    flat: "Flag level is steady: no new deterioration; handle the flagged cycles individually.",
  },
  {
    id: "root-cause", re: /significan|root cause|shifted|status of every area/,
    role: "which operating area has drifted away from normal and is the likeliest cause of a problem",
    factors: ["size of the shift versus the well's own baseline", "whether several areas shift together (common cause)", "pressure vs timing vs flow areas"],
    up: "Shifts are growing: start with the area that moved most and fix that before touching cycle timing.",
    down: "Shifts are shrinking: the earlier correction is working.",
    flat: "Shifts are stable: no new driver has appeared.",
  },
  {
    id: "forecast", re: /forecast|confidence interval|80%|ai model/,
    role: "where production or cycle behaviour is heading, and how sure the model is",
    factors: ["width of the confidence band", "gap between forecast and recent measured values", "whether the well has been recently re-tuned"],
    up: "Forecast is rising: you can plan for more gas, but verify handling capacity at the separator and line.",
    down: "Forecast is falling: look for liquid loading or depletion before it shows up as lost production.",
    flat: "Forecast is flat: current settings are expected to hold.",
  },
  {
    id: "epa", re: /ch₄|ch4|co2e|co₂e|emission|venting|epa|compliance/,
    role: "how much methane the well is releasing and whether it stays within regulatory limits",
    factors: ["number and duration of venting events", "cycle times that run longer than needed", "load factor and arrival failures that force extra venting", "wells dragging the portfolio score down"],
    up: "Emissions or risk are rising: shorten vent time, fix wells with failed arrivals, and re-check cycle timing on the worst wells first.",
    down: "Emissions or risk are falling: the changes are working; log what was changed so it can be repeated.",
    flat: "Emissions are steady: look for the 1-2 wells that dominate the total before changing anything.",
  },
  {
    id: "load-factor", re: /load factor/,
    role: "how close the plunger is to being overloaded with liquid for the available gas energy",
    factors: ["casing pressure at open", "liquid slug size after the shut-in", "line pressure", "tubing pressure"],
    up: "Load factor is rising: the well is moving toward failed arrivals. Lengthen shut-in or reduce liquid fallback.",
    down: "Load factor is falling: gas energy is comfortable; shut-in can be trimmed if production is being lost.",
    flat: "Load factor is steady: leave settings alone unless arrivals are slow.",
  },
  {
    id: "glr", re: /glr|gas-to-liquid|gas to liquid|feasib/,
    role: "whether the well makes enough gas per barrel of liquid for the lift method to work",
    factors: ["actual GLR against the minimum required GLR", "liquid rate (water cut changes)", "depth and tubing size used in the requirement"],
    up: "GLR is improving: the well has more lift margin; keep the current method.",
    down: "GLR is dropping toward the minimum: expect weaker lift; plan an assist (gas injection) or a smaller tubing/plunger option.",
    flat: "GLR is steady: the margin is stable; re-check after any change in water production.",
  },
  {
    id: "valve-mandrel", re: /mandrel|valve position|opening|depth \(ft\)/,
    role: "which gas lift valve is working, and whether the valve design matches the depth and rate",
    factors: ["time spent on each valve position", "valve opening vs recommended opening", "injection pressure vs valve port size"],
    up: "Valve activity or opening is rising: check that the upper valves are not passing gas and wasting injection.",
    down: "Valve activity or opening is falling: confirm the operating valve is still the one the design expects.",
    flat: "Valve use is stable: the design is behaving as intended.",
  },
  {
    id: "injection", re: /inject|annulus|gapl|pagl|gas lift|assist|gas-lift/,
    role: "how much lift gas is going in, and whether it is paying back in liquid or gas production",
    factors: ["injection rate and pressure stability", "liquid or gas produced for each unit of gas injected", "plunger arrival success when assist is on", "compressor or supply limits"],
    up: "Injection is rising: check production is rising with it; if not, you are over-injecting and paying for gas that gives no lift.",
    down: "Injection is falling: check supply pressure and the choke, and watch for slower arrivals or more liquid loading.",
    flat: "Injection is steady: compare liquid response against it to find the efficient rate.",
  },
  {
    id: "method-compare", re: /method|equivalent|success/,
    role: "which lift method delivers the most gas for the least cost on this well",
    factors: ["success rate of each method", "gas rate equivalent", "wells where one method clearly wins"],
    up: "The measure is rising: the method is performing; confirm it holds across wells before switching all of them.",
    down: "The measure is falling: review that method's operating settings before blaming the method.",
    flat: "The methods look similar: pick on cost and operating simplicity.",
  },
  {
    id: "velocity-dist", re: /velocity.*(frequency|range|histogram|distribution)|frequency by velocity|optimal velocity|too fast|too slow|velocity zone/,
    role: "how many plunger trips land in the safe velocity window and how many do not",
    factors: ["share of trips too slow (liquid load, low casing pressure)", "share of trips too fast (wasted gas energy, bumper/spring wear)", "line pressure and shut-in time that set the energy"],
    up: "More trips in the good window or fast tail: check which side is growing before changing anything.",
    down: "Fewer trips in the good window: re-tune shut-in time and after-flow for this well.",
    flat: "The split is steady: only adjust if a large share sits outside the window.",
    band: [350, 1200],
  },
  {
    id: "velocity", re: /velocity|ft\/min|arrival minute|arrival time/,
    role: "whether the plunger is arriving at a healthy speed on each trip",
    factors: ["casing pressure built during shut-in", "liquid slug size", "line pressure at the sales point", "shut-in time before the valve opens"],
    up: "Velocity is rising: the plunger is carrying excess energy. Shorten shut-in or open at lower casing pressure to save gas and protect the lubricator/spring.",
    down: "Velocity is falling: energy is short for the liquid load. Lengthen shut-in or reduce line pressure; check for liquid loading.",
    flat: "Velocity is steady: settings are consistent; check it is inside the safe window.",
    band: [350, 1200],
  },
  {
    id: "pc-pl", re: /pc\/pl|pl×|pc >|casing.*(vs|min required)|min required.*casing|differential|lower vs casing/,
    role: "whether casing pressure gives enough margin over line pressure for a reliable lift",
    factors: ["casing pressure at open", "line pressure (set downstream)", "tubing pressure and liquid load"],
    up: "Margin is growing: lift energy is comfortable; shut-in may be trimmed to recover production time.",
    down: "Margin is shrinking: arrivals will start to fail. Increase shut-in time or reduce line backpressure.",
    flat: "Margin is steady: no action unless wells sit below the required ratio.",
  },
  {
    id: "pressure-open", re: /pressure.*(open)|open.*pressure|open events|casing, tubing|casing psi|drop between open and close|pressure at valve/,
    role: "how much gas energy was stored when the valve opened, and how much was used",
    factors: ["casing pressure at open versus line pressure", "tubing pressure (liquid column above the plunger)", "open-to-close pressure drop (energy used per cycle)"],
    up: "Pressures are rising: check line pressure first; if casing is rising alone, shut-in may be longer than needed.",
    down: "Pressures are falling: reservoir inflow is weaker or shut-in is too short; verify before cutting cycle time further.",
    flat: "Pressures are steady: the well is cycling consistently.",
  },
  {
    id: "buildup", re: /buildup|psi gained|psi\/min|build-up/,
    role: "how fast the well recharges its casing pressure while shut in",
    factors: ["reservoir inflow strength", "liquid column height (slows buildup)", "gas leaks at wellhead or valve", "whether buildup has flattened (plateau) before the valve opens"],
    up: "Buildup is faster: the reservoir is giving energy quickly; shorter shut-in can capture more production.",
    down: "Buildup is slower: watch for liquid load or a leak; do not shorten shut-in until the cause is known.",
    flat: "Buildup is steady: use it to set the shut-in time where the curve starts to flatten.",
  },
  {
    id: "shutin-required", re: /required shut-in|min required|efficiency|plateau|hl =|liquid column|gas vs liquid travel|productive/,
    role: "whether the shut-in was long enough to lift the plunger and liquid, without wasting production time",
    factors: ["time required for gas to build pressure vs time for the liquid to travel", "estimated liquid column height", "actual shut-in versus the minimum required", "casing pressure reached before opening"],
    up: "Shut-in is running longer than required: you are idling the well; cut shut-in toward the required time.",
    down: "Shut-in is falling short of the required time: expect weak arrivals; lengthen shut-in.",
    flat: "Shut-in is close to what is required: keep the setting and monitor arrivals.",
  },
  {
    id: "shutin", re: /shut-?in/,
    role: "how long the well is closed each cycle and what that buys in pressure and production",
    factors: ["casing pressure reached versus time", "extended shut-ins vs the typical (median) one", "arrival success after long shut-ins", "buildup flattening"],
    up: "Shut-in times are growing: find out if the controller or a failed arrival is stretching them; long closed time costs production.",
    down: "Shut-in times are shrinking: check that casing pressure at open is still high enough for reliable arrivals.",
    flat: "Shut-in is steady: cycle timing is consistent.",
  },
  {
    id: "afterflow-economics", re: /break-even|retention|recycl/,
    role: "when it is cheaper to close the well and recycle than to keep flowing",
    factors: ["closing rate versus the well's average cycle rate", "how fast the rate decays during after-flow", "liquid fallback risk when casing pressure runs down"],
    up: "Closing rate stays high: the valve is closing while the well still produces; extend after-flow.",
    down: "Closing rate sits under the break-even: you are flowing past the useful point; close earlier.",
    flat: "Closing rate is steady: after-flow timing is consistent; check against break-even.",
  },
  {
    id: "afterflow", re: /after-?flow|vent/,
    role: "how the gas production window of each cycle is being used",
    factors: ["after-flow minutes versus gas produced", "vent time before close", "share of cycle spent flowing vs shut in", "casing pressure left at close"],
    up: "After-flow is getting longer: check it is adding gas; if not, you are flowing past the useful point and inviting liquid fallback.",
    down: "After-flow is getting shorter: gas may be left in the well; extend if the rate at close is still high.",
    flat: "After-flow is steady: compare with the volume per cycle to see whether it is the right length.",
  },
  {
    id: "flow-rate", re: /flow ?rate|mcf\/d|rate at close|production rate/,
    role: "how much gas the well delivers at the moment the valve closes",
    factors: ["after-flow time", "liquid load building back up", "reservoir deliverability and line pressure", "valve and separator condition"],
    up: "Rate is rising: production is improving; confirm the gain is from the well and not a line pressure drop.",
    down: "Rate is falling: check liquid loading, plunger wear or a restriction at the separator before assuming depletion.",
    flat: "Rate is steady: production is stable; focus on cycle timing efficiency.",
  },
  {
    id: "volume", re: /cumulative|volcycle|volume|production per cycle|total cycle|mcf\b/,
    role: "how much gas each cycle (and the well over time) is actually producing",
    factors: ["after-flow length", "arrival success", "shut-in time between trips", "cycles lost to failed or slow arrivals"],
    up: "Volume is climbing: confirm cycles are not getting longer to get it; production per hour matters more than per cycle.",
    down: "Volume is dropping: check for failed arrivals, liquid loading or a sensor fault before changing timing.",
    flat: "Volume is steady: the well is stable; look for small timing gains.",
  },
  {
    id: "stages", re: /cycle stages|plunger rise|stage|composition/,
    role: "where the cycle time goes: closed, plunger travel or producing",
    factors: ["shut-in share (largest in most wells)", "plunger rise time (liquid load)", "after-flow share (production window)"],
    up: "One stage is taking more of the cycle: if it is shut-in, review pressure buildup; if it is rise time, look at liquid load.",
    down: "A stage is shrinking: check it did not shorten at the cost of arrival reliability.",
    flat: "The split is steady: the cycle is well balanced; tune only the largest stage.",
  },
  {
    id: "well-compare", re: /per well|by well|well-to-well|score by well/,
    role: "which wells are doing well and which need attention first",
    factors: ["wells far from the group average", "wells outside the target range", "what separates the best from the worst well"],
    up: "Spread is wide: visit the lowest wells first; compare their settings with the best wells.",
    down: "Wells are converging: the fleet is operating consistently.",
    flat: "Wells are similar: look for small gains well by well.",
  },
];

const GENERIC_LENS = {
  id: "generic",
  generic: true,
  role: "",
  factors: [],
  up: "", down: "", flat: "",
};

function pickLens(text) {
  const t = text.toLowerCase();
  return LENSES.find((l) => l.re.test(t)) || GENERIC_LENS;
}

/* ═════════════ read the charts inside the card ═════════════ */
function collectCharts(children) {
  const charts = [];
  const visit = (node, current) => {
    React.Children.forEach(node, (el) => {
      if (!React.isValidElement(el)) return;
      const n = nameOf(el);
      let cur = current;
      if (CHART_TYPES.has(n)) {
        cur = { type: n, data: Array.isArray(el.props.data) ? el.props.data : [], series: [], xAxis: null, yAxes: [], refs: [] };
        charts.push(cur);
      } else if (cur) {
        if (SERIES_TYPES.has(n)) cur.series.push({ kind: n, props: el.props });
        else if (n === "XAxis" || n === "PolarAngleAxis") { if (!cur.xAxis) cur.xAxis = el.props; }
        else if (n === "YAxis") cur.yAxes.push(el.props);
        else if (n === "ReferenceLine" || n === "ReferenceArea") cur.refs.push({ kind: n, props: el.props });
      }
      visit(el.props && el.props.children, cur);
    });
  };
  visit(children, null);
  return charts.filter(
    (c) => c.data.length || c.series.some((s) => Array.isArray(s.props.data) && s.props.data.length)
  );
}

function sample(points, max = 14) {
  if (points.length <= max) return points;
  const keep = new Set([0, points.length - 1]);
  let mi = 0, ma = 0;
  points.forEach((p, i) => {
    if (p.y < points[mi].y) mi = i;
    if (p.y > points[ma].y) ma = i;
  });
  keep.add(mi); keep.add(ma);
  const step = (points.length - 1) / (max - 4);
  for (let k = 0; k < max - 4; k++) keep.add(Math.round(k * step));
  return [...keep].sort((a, b) => a - b).map((i) => points[i]);
}

function pearson(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0, syy = 0;
  for (let i = 0; i < n; i++) {
    sxy += (xs[i] - mx) * (ys[i] - my);
    sxx += (xs[i] - mx) ** 2;
    syy += (ys[i] - my) ** 2;
  }
  if (!sxx || !syy) return null;
  return sxy / Math.sqrt(sxx * syy);
}

function describeSeries(chart, s, lens, opts) {
  const p = s.props;
  const rows = Array.isArray(p.data) && p.data.length ? p.data : chart.data;
  const xKey = chart.xAxis && typeof chart.xAxis.dataKey === "string" ? chart.xAxis.dataKey : null;
  const yAxis = chart.yAxes[0] || {};
  const base = {
    kind: s.kind,
    name: p.name || (typeof p.dataKey === "string" ? p.dataKey : s.kind),
    unit: p.unit || "",
  };

  if (s.kind === "Pie") {
    const vKey = typeof p.dataKey === "string" ? p.dataKey : "value";
    const nKey = p.nameKey || "name";
    const items = rows.map((r) => ({ x: r[nKey], y: r[vKey] })).filter((r) => isNum(r.y));
    if (!items.length) return null;
    const total = items.reduce((a, b) => a + b.y, 0);
    return {
      ...base, n: items.length, total: rnd(total),
      points: items.slice(0, 12).map((i) => ({ x: i.x, y: rnd(i.y), share_pct: total ? rnd((i.y / total) * 100) : null })),
    };
  }

  if (s.kind === "Scatter") {
    const xk = xKey || "x";
    const yk = typeof yAxis.dataKey === "string" ? yAxis.dataKey : (typeof p.dataKey === "string" ? p.dataKey : "y");
    const pts = rows.map((r) => ({ x: r[xk], y: r[yk] })).filter((r) => isNum(r.x) && isNum(r.y));
    if (!pts.length) return null;
    const ys = pts.map((q) => q.y), xs = pts.map((q) => q.x);
    const r = pearson(xs, ys);
    return {
      ...base, name: p.name || yk, x_field: xk, y_field: yk, n: pts.length,
      x_range: [rnd(Math.min(...xs)), rnd(Math.max(...xs))],
      y_range: [rnd(Math.min(...ys)), rnd(Math.max(...ys))],
      y_mean: rnd(ys.reduce((a, b) => a + b, 0) / ys.length),
      correlation: r == null ? null : rnd(r),
      points: sample(pts.map((q) => ({ x: q.x, y: q.y }))).map((q) => ({ x: rnd(q.x), y: rnd(q.y) })),
    };
  }

  if (typeof p.dataKey !== "string") return null;
  const pts = [];
  rows.forEach((r, i) => {
    const v = r[p.dataKey];
    if (isNum(v)) pts.push({ x: xKey != null && r[xKey] != null ? r[xKey] : i + 1, y: v, i });
  });
  if (!pts.length) return null;
  const ys = pts.map((q) => q.y);
  const min = Math.min(...ys), max = Math.max(...ys);
  const mean = ys.reduce((a, b) => a + b, 0) / ys.length;
  const first = ys[0], last = ys[ys.length - 1];
  const minAt = pts[ys.indexOf(min)].x, maxAt = pts[ys.indexOf(max)].x;

  let trend = "flat";
  if (pts.length >= 3 && max !== min) {
    const n = pts.length;
    const mx = (n - 1) / 2;
    let sxy = 0, sxx = 0;
    ys.forEach((y, i) => { sxy += (i - mx) * (y - mean); sxx += (i - mx) ** 2; });
    const rel = ((sxy / sxx) * (n - 1)) / (max - min);
    trend = rel > 0.15 ? "rising" : rel < -0.15 ? "falling" : "flat";
  }
  // opt-in (trendAware): a flat straight-line fit can hide large bar-to-bar swings.
  // Only plots that ask for it get these extra fields, so every other plot is unchanged.
  let swingInfo = null;
  if (opts && opts.trendAware && pts.length >= 6 && max !== min) {
    const range = max - min;
    const sd = Math.sqrt(ys.reduce((a, y) => a + (y - mean) ** 2, 0) / ys.length);
    let swings = 0;
    for (let k = 1; k < ys.length; k++) if (Math.abs(ys[k] - ys[k - 1]) >= 0.2 * range) swings++;
    if (trend === "flat" && range >= 20 && sd / range >= 0.15) swingInfo = { net_trend: "flat", std_dev: rnd(sd), big_swings: swings };
  }
  const out = {
    ...base, n: pts.length,
    min: rnd(min), min_at: minAt, max: rnd(max), max_at: maxAt,
    mean: rnd(mean), first: rnd(first), last: rnd(last),
    change_pct: first !== 0 ? rnd(((last - first) / Math.abs(first)) * 100) : null,
    trend: swingInfo ? "fluctuating" : trend,
    ...(swingInfo || {}),
    points: sample(pts).map((q) => ({ x: q.x, y: rnd(q.y) })),
  };
  // share of points outside the plot-specific safe band (e.g. plunger velocity window)
  if (lens && lens.band && lens.band.length === 2 && s.kind !== "Bar") {
    const [lo, hi] = lens.band;
    const inBand = (v) => v >= lo && v <= hi;
    if (min < hi * 1.5 && max > lo * 0.2) {
      out.below_band_pct = rnd((ys.filter((v) => v < lo).length / ys.length) * 100);
      out.above_band_pct = rnd((ys.filter((v) => v > hi).length / ys.length) * 100);
      out.in_band_pct = rnd((ys.filter(inBand).length / ys.length) * 100);
    }
  }
  return out;
}

function flattenText(node) {
  let out = "";
  React.Children.forEach(node, (n) => {
    if (typeof n === "string" || typeof n === "number") out += n;
    else if (React.isValidElement(n)) out += flattenText(n.props.children);
  });
  return out.replace(/\s+/g, " ").trim();
}

function buildPayload(title, subtitle, children, lensId, opts) {
  const sub = typeof subtitle === "string" ? subtitle : "";
  const charts0 = collectCharts(children).slice(0, 3);

  // text the lens is matched on = title + subtitle + series / axis names
  const names = [];
  charts0.forEach((c) => {
    c.series.forEach((s) => {
      names.push(s.props.name, s.props.dataKey);
    });
    if (c.xAxis) names.push(labelText(c.xAxis.label));
    c.yAxes.forEach((y) => names.push(labelText(y.label), y.name));
  });
  const hay = [title, sub, ...names].filter((x) => typeof x === "string").join(" ");
  const lens = lensId ? lensById(lensId) : pickLens(`${title || ""} ${sub} ${hay}`);
  const scope = /per well|by well|each well|well-to-well/i.test(hay) ? "multi-well comparison" : "single well";

  const charts = charts0.map((c) => {
    const seen = new Set();
    const series = c.series
      .filter((s) => s.props.legendType !== "none" && !s.props.hide)
      .map((s) => describeSeries(c, s, lens, opts))
      .filter(Boolean)
      .filter((s) => {
        const k = `${s.kind}|${s.name}|${s.y_field || ""}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, 8);
    const refs = c.refs
      .map((r) => {
        const p = r.props;
        if (r.kind === "ReferenceLine") {
          if (isNum(p.y)) return { axis: "y", value: rnd(p.y), label: labelText(p.label) };
          if (isNum(p.x) || typeof p.x === "string") return { axis: "x", value: p.x, label: labelText(p.label) };
        } else if (isNum(p.y1) && isNum(p.y2)) {
          return { axis: "y-band", from: rnd(p.y1), to: rnd(p.y2), label: labelText(p.label) };
        }
        return null;
      })
      .filter(Boolean)
      .slice(0, 6);
    return {
      chart_type: c.type,
      x_axis: c.xAxis ? { field: typeof c.xAxis.dataKey === "string" ? c.xAxis.dataKey : "", label: labelText(c.xAxis.label), unit: c.xAxis.unit || "" } : null,
      y_axes: c.yAxes.map((y) => ({ field: typeof y.dataKey === "string" ? y.dataKey : "", label: labelText(y.label) || y.name || "", unit: y.unit || "" })).slice(0, 3),
      rows: c.data.length,
      series,
      reference_lines: refs,
    };
  }).filter((c) => c.series.length);

  return {
    title: title || "",
    subtitle: sub,
    scope,
    lens: lens.generic
      ? { id: "generic", band: null }
      : { id: lens.id, band: lens.band || null },
    charts,
  };
}

/* ═════════════ local fallback (same lens + same data) ═════════════ */
const lensById = (id) => LENSES.find((l) => l.id === id) || GENERIC_LENS;

function headline(s) {
  const u = s.unit ? ` ${s.unit}` : "";
  if (s.kind === "Pie") {
    const top = [...s.points].sort((a, b) => b.y - a.y)[0];
    return `"${top.x}" is the largest share at ${top.share_pct}% of ${s.n} groups.`;
  }
  if (s.kind === "Scatter") {
    const c = s.correlation;
    const rel = c == null ? "" : Math.abs(c) < 0.3 ? "little relation" : `a ${Math.abs(c) > 0.7 ? "strong" : "moderate"} ${c > 0 ? "positive" : "negative"} relation`;
    return `${s.name} runs ${s.y_range[0]}–${s.y_range[1]}${u} (average ${s.y_mean})${rel ? `, with ${rel} to ${s.x_field}` : ""}.`;
  }
  const move = s.change_pct == null ? "" : ` (${s.change_pct > 0 ? "+" : ""}${s.change_pct}% from start to end)`;
  const word = s.trend === "rising" ? "trending up" : s.trend === "falling" ? "trending down" : s.trend === "fluctuating" ? "fluctuating with no sustained rise or fall" : "holding steady";
  return `${s.name} averages ${s.mean}${u}, from ${s.min} (at ${s.min_at}) to ${s.max} (at ${s.max_at}), ${word}${move}.`;
}

/* Field action: built only from this plot's own numbers and series names. Never empty. */
function buildAction(payload) {
  const series = payload.charts.flatMap((c) => c.series);
  const refs = payload.charts.flatMap((c) => c.reference_lines).filter((r) => r.axis === "y");
  const lens = lensById(payload.lens && payload.lens.id);
  const band = lens.band;
  if (!series.length) return "Plot more cycles before acting on this plot.";

  if (series.every((s) => s.kind === "Pie")) {
    const pts = [...series[0].points].sort((a, b) => b.y - a.y);
    const top = pts[0];
    return `Open the cycles in "${top.x}" (${top.y} of ${series[0].total}, ${top.share_pct}%) and compare them with the other groups.`;
  }
  const p = series.find((s) => s.kind !== "Pie") || series[0];
  const name = p.name || "The series";
  const u = p.unit ? ` ${p.unit}` : "";

  if (p.kind === "Scatter") {
    let t = `Check the cycles at the extreme ${p.y_field || "y"} values (${p.y_range[0]} to ${p.y_range[1]}${u}).`;
    if (p.correlation != null && Math.abs(p.correlation) >= 0.7) t += ` ${p.x_field} and ${p.y_field} move together (r = ${p.correlation}), so a fix on one should show on the other.`;
    return t;
  }

  const parts = [];
  if (p.in_band_pct != null && band) {
    const b = p.below_band_pct || 0, a = p.above_band_pct || 0;
    if (b >= 20 && b >= a) parts.push(`${b}% of ${name} sits below the ${band[0]}-${band[1]} window: review the cycles near the low of ${p.min}${u} at ${p.min_at}.`);
    else if (a >= 20) parts.push(`${a}% of ${name} sits above the ${band[0]}-${band[1]} window: review the cycles near the high of ${p.max}${u} at ${p.max_at}.`);
    else parts.push(`${p.in_band_pct}% of ${name} is inside the ${band[0]}-${band[1]} window: keep current operation and recheck if that share drops.`);
  }
  if (p.min === 0 && p.mean > 0) parts.push(`Verify the zero values of ${name} at ${p.min_at} (real no-flow vs missing data) before trusting the trend.`);
  if (p.mean > 0 && p.max > 3 * p.mean) parts.push(`Check the point at ${p.max_at} (${p.max}${u}, ${rnd(p.max / p.mean)}x the mean of ${p.mean}): confirm whether it is a real event or a bad reading.`);
  if (refs.length && p.last != null) {
    const r0 = refs[0];
    const rel = p.last > r0.value ? "above" : p.last < r0.value ? "below" : "at";
    parts.push(`Last ${name} value ${p.last}${u} is ${rel} the reference line at ${r0.value}${r0.label ? ` (${r0.label})` : ""}.`);
  }
  const fc = series.find((s) => /forecast/i.test(String(s.name || "")));
  const ac = series.find((s) => s !== fc && s.kind !== "Pie");
  if (fc && ac && fc.first != null && ac.last != null) parts.push(`Forecast starts at ${fc.first} against a last measured value of ${ac.last}: confirm the starting level is realistic before using it.`);
  if (!parts.length) {
    const ch = p.change_pct != null ? Math.abs(p.change_pct) : null;
    const pct = ch != null ? ` (${ch}% ${p.trend === "rising" ? "higher" : "lower"})` : "";
    if (p.trend === "rising") parts.push(`${name} went from ${p.first} to ${p.last}${u}${pct}: check the cycles after the low of ${p.min} at ${p.min_at} to see where the climb started.`);
    else if (p.trend === "falling") parts.push(`${name} went from ${p.first} to ${p.last}${u}${pct}: check the cycles after the high of ${p.max} at ${p.max_at} to see where the drop started.`);
    else if (p.trend === "fluctuating") parts.push(`${name} swings between ${p.min} and ${p.max}${u} (${p.big_swings} large cycle-to-cycle jumps): review the cycles around the low of ${p.min} at ${p.min_at} and the high of ${p.max} at ${p.max_at}.`);
    else parts.push(`${name} is steady around ${p.mean}${u}: keep current operation and recheck if it leaves ${p.min} to ${p.max}.`);
  }
  return parts.slice(0, 2).join(" ");
}

function localSummary(payload) {
  const lens = lensById(payload.lens && payload.lens.id);
  const all = payload.charts.flatMap((c) => c.series);
  const primary = all.find((s) => s.kind !== "Pie") || all[0];
  if (!primary) return [];

  let reading, drivers;
  if (primary.kind === "Pie") {
    const pts = [...primary.points].sort((a, b) => b.y - a.y);
    const top = pts[0], second = pts[1], last = pts[pts.length - 1];
    reading = `${pts.map((p) => `${p.x} ${p.share_pct}%`).join(", ")} (${primary.total} total).`;
    drivers = second
      ? `"${top.x}" is the largest group at ${top.share_pct}%, ${rnd(top.share_pct - second.share_pct)} points ahead of "${second.x}"; "${last.x}" is the smallest at ${last.share_pct}%.`
      : `Only one group is plotted: "${top.x}".`;
  } else {
    const u = primary.unit ? ` ${primary.unit}` : "";
    reading = headline(primary);
    if (primary.in_band_pct != null && lens.band) {
      reading += ` ${primary.in_band_pct}% of points sit inside the ${lens.band[0]}-${lens.band[1]} window (${primary.below_band_pct}% below, ${primary.above_band_pct}% above).`;
    }
    // Drivers: only the series plotted here, with their own numbers
    if (primary.kind === "Scatter") {
      drivers = primary.correlation == null
        ? "Too few points to say how the two plotted measures move together."
        : `Correlation between ${primary.x_field} and ${primary.y_field} is ${primary.correlation}.`;
    } else {
      drivers = `${primary.name} peaks at ${primary.max}${u} (${primary.max_at}) and is lowest at ${primary.min}${u} (${primary.min_at}); last value ${primary.last}${u}.`;
      const others = all.filter((s) => s !== primary && s.kind !== "Pie" && s.kind !== "Scatter");
      if (others.length) {
        const o = others[0];
        drivers += ` ${o.name} peaks at ${o.max} (${o.max_at}) and is lowest at ${o.min} (${o.min_at}).`;
      }
    }
  }
  return [`Reading: ${reading}`, `Drivers: ${drivers}`, `Action: ${buildAction(payload)}`];
}

const LABELS = { reading: "What it means", drivers: "What drives it", action: "Field action" };
function parseSummary(text) {
  const clean = String(text || "").replace(/\*\*|__/g, "").replace(/[•◆▲■▶]/g, " ");
  const marks = [...clean.matchAll(/(reading|drivers|action)\s*:/gi)];
  const out = marks
    .map((m, i) => ({
      key: m[1].toLowerCase(),
      text: clean.slice(m.index + m[0].length, i + 1 < marks.length ? marks[i + 1].index : clean.length).replace(/\s+/g, " ").trim(),
    }))
    .filter((it) => it.text);
  // all three sections are required; otherwise the caller falls back to the local three-part summary
  return ["reading", "drivers", "action"].every((k) => out.some((it) => it.key === k)) ? out : [];
}

/* ═════════════ component ═════════════ */
export default function PlotSummary({ title, subtitle, children, accent, inline, lensId, trendAware }) {
  const theme = useTheme();
  const C = getColors(theme ? theme.isDark : true);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");

  const hasPlot = collectCharts(children).length > 0;
  const payload = open && hasPlot ? buildPayload(title, subtitle, children, lensId, { trendAware }) : null;
  const key = payload ? JSON.stringify(payload) : "";

  useEffect(() => {
    if (!open || !payload) return undefined;
    if (!payload.charts.length) {
      setItems([{ key: "reading", text: "No plotted data available for this chart yet." }]);
      setSource("local");
      setLoading(false);
      return undefined;
    }
    if (summaryCache[key]) {
      setItems(summaryCache[key].items);
      setSource(summaryCache[key].source);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      let result;
      try {
        const res = await axios.post(`${API}/plot-summary`, payload, { timeout: SUMMARY_TIMEOUT_MS });
        const got = parseSummary(res.data && res.data.summary);
        result = got.length ? { items: got, source: "ai" } : { items: parseSummary(localSummary(payload).join("\n")), source: "local" };
      } catch (e) {
        result = { items: parseSummary(localSummary(payload).join("\n")), source: "local" };
      }
      summaryCache[key] = result;
      if (cancelled) return;
      setItems(result.items);
      setSource(result.source);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key]);

  if (!hasPlot) return null;
  const ac = accent || C.cyan;
  const labelColor = { reading: ac, drivers: C.amber, action: C.green };

  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={open ? "Hide plot summary" : "Show plot summary"}
        style={{
          ...(inline ? { position: "relative", flexShrink: 0, marginTop: INLINE_GAP_PX, marginBottom: open ? 0 : INLINE_GAP_PX } : { position: "absolute", top: 12, right: 14, zIndex: 5 }),
          display: "flex", alignItems: "center", gap: 7,
          background: "transparent", border: "none", padding: 0, cursor: "pointer",
          fontFamily: MONO, fontSize: SUMMARY_LABEL_PX, fontWeight: 700, letterSpacing: 0.4,
          color: open ? ac : C.muted,
        }}
      >
        <span>Summary</span>
        <span style={{
          position: "relative", width: 38, height: 20, borderRadius: 12,
          background: open ? `${ac}40` : `${C.muted}30`,
          border: `1px solid ${open ? ac : C.muted}`, transition: "all .18s",
        }}>
          <span style={{
            position: "absolute", top: 2.5, left: open ? 20 : 3, width: 13, height: 13, borderRadius: "50%",
            background: open ? ac : C.muted, transition: "left .18s",
          }} />
        </span>
      </button>

      {open && (
        <div
          style={{
            marginTop: 12, marginBottom: inline ? INLINE_GAP_PX : 0, padding: "12px 14px", borderRadius: 10,
            background: `${ac}0d`, border: `1px solid ${ac}33`, borderLeft: `3px solid ${ac}`,
            fontFamily: MONO,
          }}
        >
          {loading ? (
            <div style={{ fontSize: 11, color: C.muted }}>Reading this plot…</div>
          ) : (
            items.map((it, i) => (
              <div key={i} style={{ marginBottom: i === items.length - 1 ? 0 : 9 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: labelColor[it.key] || ac, marginBottom: 2 }}>
                  {LABELS[it.key] || it.key}
                </div>
                <div style={{ fontSize: 11, lineHeight: 1.6, color: C.text }}>{it.text}</div>
              </div>
            ))
          )}
          {!loading && source && (
            <div style={{ marginTop: 8, fontSize: 9, color: C.muted2 }}>
              {source === "ai" ? "AI summary · based only on this plot's data" : "Computed from this plot's data"}
            </div>
          )}
        </div>
      )}
    </>
  );
}